import React, { Component, ErrorInfo, ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import "./ErrorBoundary.css";

export interface ErrorBoundaryProps {
  /**
   * The content to be rendered
   */
  children: ReactNode;

  /**
   * Optional custom error fallback component
   */
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;

  /**
   * Optional error message to display
   */
  errorMessage?: string;

  /**
   * Optional function to handle errors
   */
  onError?: (_error: Error, _errorInfo: ErrorInfo) => void;

  /**
   * Additional CSS class name
   */
  className?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary component catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the component tree.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log the error to an error reporting service
    console.error("Error caught by ErrorBoundary:", error, info);

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, info);
    }
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { children, fallback, errorMessage, className } = this.props;
    const { hasError, error } = this.state;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        const FallbackComponent = fallback;
        return <FallbackComponent error={error} resetError={this.resetError} />;
      }

      // Default error UI
      return (
        <div className={`error-boundary ${className || ""}`} data-testid="error-boundary">
          <div className="error-icon">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <h3 className="error-title">Something went wrong</h3>
          <p className="error-message">
            {errorMessage || "An error occurred while rendering this component"}
          </p>
          <div className="error-details">
            <details>
              <summary>Error Details</summary>
              <pre>{error.toString()}</pre>
              {error.stack && <pre className="error-stack">{error.stack}</pre>}
            </details>
          </div>
          <button
            className="error-reset-button"
            onClick={this.resetError}
            data-testid="error-reset-button"
          >
            Try Again
          </button>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
